import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { ReactiveFormsModule } from '@angular/forms';
import {
  WorkTimerecord,
  WorkTimerecordBalancesV2Service,
  WorkTimerecorddetail,
  WorkTimerecorddetailsByDateDto,
  WorkTimerecorddetailsV2Service,
  WorkTimerecordSaldoDto,
  WorkUsersV2Service,
  WorkUserV2,
} from '@work-frontend-monorepo/work-rest-api';
import { lastValueFrom } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import dayjs from 'dayjs';
import { ArbeitszeitCreateUpdateModalComponent } from '../../components/arbeitszeit-create-update-modal/arbeitszeit-create-update-modal.component';
import { ArbeitszeitStundensaldoComponent } from '../../components/arbeitszeit-stundensaldo/arbeitszeit-stundensaldo.component';
import { ArbeitszeitTageskarteComponent } from '../../components/arbeitszeit-tageskarte/arbeitszeit-tageskarte.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { DropdownOption, SelectComponent } from '@work-frontend-monorepo/work-ui/forms';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';

@Component({
  selector: 'sfg-app-arbeitszeit-uebersicht-page',
  templateUrl: './arbeitszeit-uebersicht-page.component.html',
  styleUrls: ['./arbeitszeit-uebersicht-page.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    ReactiveFormsModule,
    TranslatePipe,
    ArbeitszeitCreateUpdateModalComponent,
    ArbeitszeitStundensaldoComponent,
    ArbeitszeitTageskarteComponent,
    SelectComponent,
  ],
})
export class ArbeitszeitUebersichtPageComponent {
  // Viewchilds
  arbeitszeitCreateUpdateModal = viewChild<ArbeitszeitCreateUpdateModalComponent>('arbeitszeitCreateUpdateModal');
  // Signal & State
  timerecorddetails = signal<WorkTimerecorddetailsByDateDto[]>([]);
  balance = signal<WorkTimerecordSaldoDto | undefined>(undefined);
  projectedBalance = computed<WorkTimerecordSaldoDto | undefined>(() => {
    const balance = this.balance();
    if (!balance) return undefined;

    const today = dayjs().format('YYYY-MM-DD');
    const todayEntry = this.timerecorddetails().find((d) => d.date === today);
    if (!todayEntry) return balance;

    const adjustment = Math.max(0, (todayEntry.plannedHours ?? 0) - (todayEntry.actualHours ?? 0));
    return { saldo: balance.saldo + adjustment };
  });
  selectedTimerecord = signal<WorkTimerecord | undefined>(undefined);
  selectedTimerecorddetail = signal<WorkTimerecorddetail | undefined>(undefined);
  userId = signal<number | undefined>(undefined);
  supervisedUsers = signal<WorkUserV2[]>([]);
  userDropdownOptions = signal<DropdownOption[]>([]);
  selectedUserId = signal<number | undefined>(undefined);
  isSupervisor = signal<boolean>(false);
  currentWeekOffset = signal<number>(0); // 0 = aktuelle Woche, negative = zurück, positive = vorwärts
  currentWeekLabel = signal<{ week: number; year: number } | null>(null);
  currentWeekDateRange = signal<{ start: string; end: string } | null>(null);
  // Injections
  private readonly store = inject(Store);
  profile = toSignal(this.store.select(ProfileState.profile));
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workTimerecorddetailsV2Service = inject(WorkTimerecorddetailsV2Service);
  private readonly workTimerecordBalancesV2Service = inject(WorkTimerecordBalancesV2Service);
  private readonly workUsersV2Service = inject(WorkUsersV2Service);
  private profileEffect = effect(async () => {
    const profile = this.profile();
    if (profile) {
      // Prüfe, ob User ein Supervisor ist
      if (profile.supervisedUserIds && profile.supervisedUserIds.length > 0) {
        this.isSupervisor.set(true);
        await this.loadSupervisedUsers(profile.supervisedUserIds);
      } else {
        this.isSupervisor.set(false);
        this.userId.set(profile.id);
        this.selectedUserId.set(profile.id);
        await Promise.all([this.loadTimerecorddetails(), this.loadBalance(profile.id)]);
      }
    }
  });

  protected createForTimerecord(timerecord?: WorkTimerecord) {
    this.selectedTimerecord.set(timerecord);
    this.selectedTimerecorddetail.set(undefined);
    this.arbeitszeitCreateUpdateModal()?.modal()?.open();
  }

  protected async deleteTimerecorddetail(timerecorddetail: WorkTimerecorddetail) {
    try {
      await lastValueFrom(
        this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerDelete({ id: timerecorddetail.id, body: timerecorddetail }),
      );
      await this.loadTimerecorddetails();
    } catch (deleteTimerecorddetailException) {
      this.exceptionHandlingService.handleExceptionWithAlert('deleteTimerecorddetailException', deleteTimerecorddetailException);
    }
  }

  protected async reapplyPauseTemplate(timerecord: WorkTimerecord) {
    try {
      if (timerecord.userId && timerecord.workdate) {
        await lastValueFrom(
          this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerReapplyPauseTemplate({
            userId: timerecord.userId,
            date: timerecord.workdate,
          }),
        );
      }
      await this.loadTimerecorddetails();
    } catch (reapplyPauseTemplateException) {
      this.exceptionHandlingService.handleExceptionWithAlert('reapplyPauseTemplateException', reapplyPauseTemplateException);
    }
  }

  protected async resetDay(timerecord: WorkTimerecord) {
    try {
      if (timerecord.workdate && timerecord.userId) {
        await lastValueFrom(
          this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerResetDay({
            userId: timerecord.userId,
            date: timerecord.workdate,
          }),
        );
      }
      await this.loadTimerecorddetails();
    } catch (resetDayException) {
      this.exceptionHandlingService.handleExceptionWithAlert('resetDayException', resetDayException);
    }
  }

  protected editTimerecorddetail(timerecorddetail: WorkTimerecorddetail) {
    this.selectedTimerecorddetail.set(timerecorddetail);
    this.selectedTimerecord.set(timerecorddetail.timerecord);
    this.arbeitszeitCreateUpdateModal()?.modal()?.open();
  }

  protected goToPreviousWeek() {
    const newOffset = this.currentWeekOffset() - 1;
    this.currentWeekOffset.set(newOffset);
    this.updateWeekNavigation();
    this.loadTimerecorddetails();
  }

  protected goToNextWeek() {
    const newOffset = this.currentWeekOffset() + 1;
    this.currentWeekOffset.set(newOffset);
    this.updateWeekNavigation();
    this.loadTimerecorddetails();
  }

  protected async onUserChange(userId: number) {
    this.selectedUserId.set(userId);
    this.userId.set(userId);
    await Promise.all([this.loadTimerecorddetails(), this.loadBalance(userId)]);
  }

  protected async loadTimerecorddetails() {
    const userId = this.userId();
    if (userId) {
      try {
        const offset = this.currentWeekOffset();
        const startOfWeek = dayjs().add(offset, 'week').startOf('week');
        const endOfWeek = dayjs().add(offset, 'week').endOf('week');

        this.updateWeekNavigation();

        const timerecorddetailsResponseByDate = await lastValueFrom(
          this.workTimerecorddetailsV2Service.timerecorddetailsV2ControllerFindTimerecorddetailsByUserIdAndDaterange({
            userId: userId,
            dateFrom: startOfWeek.format('YYYY-MM-DD'),
            dateTo: endOfWeek.format('YYYY-MM-DD'),
          }),
        );
        this.timerecorddetails.set(timerecorddetailsResponseByDate);
      } catch (loadUserDetailsException) {
        this.exceptionHandlingService.handleExceptionWithAlert('loadUserDetailsException', loadUserDetailsException);
      }
    }
  }

  private async loadBalance(userId: number) {
    try {
      const today = dayjs().format('YYYY-MM-DD');
      const saldo = await lastValueFrom(
        this.workTimerecordBalancesV2Service.timerecordBalancesV2ControllerFindSaldoByUserIdAndDate({ userId, date: today }),
      );
      this.balance.set(saldo);
    } catch (loadBalanceException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadBalanceException', loadBalanceException);
    }
  }

  private updateWeekNavigation() {
    const offset = this.currentWeekOffset();
    const startOfWeek = dayjs().add(offset, 'week').startOf('week');
    const endOfWeek = dayjs().add(offset, 'week').endOf('week');

    // Kalenderwoche und Jahr
    const weekNumber = startOfWeek.isoWeek();
    const year = startOfWeek.year();

    this.currentWeekLabel.set({ week: weekNumber, year });
    this.currentWeekDateRange.set({ start: startOfWeek.format('DD.MM.YYYY'), end: endOfWeek.format('DD.MM.YYYY') });
  }

  private async loadSupervisedUsers(supervisedUserIds: number[]) {
    try {
      const profile = this.profile();
      const users: WorkUserV2[] = [];
      const dropdownOptions: DropdownOption[] = [];

      // Füge zuerst den eingeloggten User selbst hinzu
      if (profile) {
        const currentUser = await lastValueFrom(this.workUsersV2Service.usersV2ControllerFindOneById({ id: profile.id }));
        users.push(currentUser);
        dropdownOptions.push({
          label: `${currentUser.firstName} ${currentUser.lastName}`,
          value: currentUser.id,
          name: `${currentUser.firstName} ${currentUser.lastName}`,
        });
      }

      // Lade Details für jeden supervisierten User
      for (const userId of supervisedUserIds) {
        const user = await lastValueFrom(this.workUsersV2Service.usersV2ControllerFindOneById({ id: userId }));
        users.push(user);
        dropdownOptions.push({
          label: `${user.firstName} ${user.lastName}`,
          value: user.id,
          name: `${user.firstName} ${user.lastName}`,
        });
      }

      this.supervisedUsers.set(users);
      this.userDropdownOptions.set(dropdownOptions);

      // Setze den eingeloggten User (erster in der Liste) als ausgewählt
      if (users.length > 0 && profile) {
        this.selectedUserId.set(profile.id);
        this.userId.set(profile.id);
        await Promise.all([this.loadTimerecorddetails(), this.loadBalance(profile.id)]);
      }
    } catch (loadSupervisedUsersException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadSupervisedUsersException', loadSupervisedUsersException);
    }
  }
}
